import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, ArrowLeft, ArrowRight, Bot, Mic, Code2, Sparkles, Trophy, CheckCircle2, BookOpen, Users, Laptop } from 'lucide-react';
import HeroSlider from '../components/shared/HeroSlider';
import Footer from '../components/shared/Footer';

export default function HowItWorks() {
  const { t, i18n } = useTranslation('howItWorks');
  const isRTL = i18n.language === 'ar';

  // Hero slides data - single slide only
  const heroSlides = [
    {
      title: t('hero.title'),
      highlight: t('hero.titleHighlight') + ' ' + t('hero.titleEnd'),
      description: t('hero.description'),
      image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    },
  ];

  return (
    <div className={`min-h-screen bg-white font-sans text-slate-900 selection:bg-blue-100 flex flex-col overflow-y-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* Hero Slider - Same as Homepage */}
      <HeroSlider slides={heroSlides} currentPage="how-it-works" />

      {/* The Core Loop */}
      <section className="py-24 px-6 relative overflow-hidden">
        {/* Connecting Line (Desktop) */}
        <div className="hidden md:block absolute left-1/2 top-32 bottom-32 w-0.5 bg-gradient-to-b from-blue-100 via-purple-100 to-slate-100 -translate-x-1/2 z-0"></div>

        <div className="max-w-5xl mx-auto space-y-24 relative z-10">
            
            {/* Step 1: Content - Text on LEFT, Icon on RIGHT */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="order-2 md:order-1 md:pr-12" style={{ textAlign: isRTL ? 'right' : 'right' }}>
                    <div className={`inline-flex items-center gap-2 mb-4 text-blue-600 font-semibold ${isRTL ? 'flex-row-reverse float-right' : ''}`}>
                        <span className="bg-blue-100 w-8 h-8 rounded-full flex items-center justify-center text-sm">{t('steps.step1.number')}</span>
                        <span>{t('steps.step1.label')}</span>
                    </div>
                    <div className="clear-both"></div>
                    <h3 className="text-3xl font-bold text-slate-900 mb-4">{t('steps.step1.title')}</h3>
                    <p className="text-lg text-slate-600 leading-relaxed">
                        {t('steps.step1.description')}
                        <span className="font-semibold text-slate-800"> {t('steps.step1.course1')}</span> {t('steps.step1.or')}
                        <span className="font-semibold text-slate-800"> {t('steps.step1.course2')}</span>
                        {t('steps.step1.descriptionEnd')}
                    </p>
                </div>
                <div className="order-1 md:order-2 flex justify-center md:justify-start md:pl-12">
                    <div className="w-24 h-24 bg-blue-50 rounded-2xl flex items-center justify-center shadow-sm border border-blue-100 transform rotate-3">
                        <BookOpen className="w-10 h-10 text-blue-600" />
                    </div>
                </div>
            </div>

            {/* Step 2: The Agent - Icon on LEFT, Text on RIGHT */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="order-1 flex justify-center md:justify-end md:pr-12">
                    <div className="w-24 h-24 bg-purple-50 rounded-2xl flex items-center justify-center shadow-sm border border-purple-100 transform -rotate-3">
                        <Bot className="w-10 h-10 text-purple-600" />
                    </div>
                </div>
                <div className="order-2 md:pl-12" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                     <div className={`inline-flex items-center gap-2 mb-4 text-purple-600 font-semibold ${isRTL ? 'flex-row-reverse float-right' : ''}`}>
                        <span className="bg-purple-100 w-8 h-8 rounded-full flex items-center justify-center text-sm">{t('steps.step2.number')}</span>
                        <span>{t('steps.step2.label')}</span>
                    </div>
                    <div className="clear-both"></div>
                    <h3 className="text-3xl font-bold text-slate-900 mb-4">{t('steps.step2.title')}</h3>
                    <p className="text-lg text-slate-600 leading-relaxed">
                        {t('steps.step2.description')}
                    </p>
                </div>
            </div>

            {/* Step 3: Interaction - Text on LEFT, Icon on RIGHT */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="order-2 md:order-1 md:pr-12" style={{ textAlign: isRTL ? 'right' : 'right' }}>
                     <div className={`inline-flex items-center gap-2 mb-4 text-indigo-600 font-semibold ${isRTL ? 'flex-row-reverse float-right' : ''}`}>
                        <span className="bg-indigo-100 w-8 h-8 rounded-full flex items-center justify-center text-sm">{t('steps.step3.number')}</span>
                        <span>{t('steps.step3.label')}</span>
                    </div>
                    <div className="clear-both"></div>
                    <h3 className="text-3xl font-bold text-slate-900 mb-4">{t('steps.step3.title')}</h3>
                    <p className="text-lg text-slate-600 leading-relaxed">
                        {t('steps.step3.description')} <span className="italic">&ldquo;{t('steps.step3.example1')}&rdquo;</span> {t('steps.step3.or')} <span className="italic">&ldquo;{t('steps.step3.example2')}&rdquo;</span> {t('steps.step3.descriptionEnd')}
                    </p>
                </div>
                <div className="order-1 md:order-2 flex justify-center md:justify-start md:pl-12">
                    <div className="w-24 h-24 bg-indigo-50 rounded-2xl flex items-center justify-center shadow-sm border border-indigo-100 transform rotate-3">
                        <Mic className="w-10 h-10 text-indigo-600" />
                    </div>
                </div>
            </div>

             {/* Step 4: Facilitation - Icon on LEFT, Text on RIGHT */}
             <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="order-1 flex justify-center md:justify-end md:pr-12">
                    <div className="w-24 h-24 bg-green-50 rounded-2xl flex items-center justify-center shadow-sm border border-green-100 transform -rotate-3">
                        <Users className="w-10 h-10 text-green-600" />
                    </div>
                </div>
                <div className="order-2 md:pl-12" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                     <div className={`inline-flex items-center gap-2 mb-4 text-green-600 font-semibold ${isRTL ? 'flex-row-reverse float-right' : ''}`}>
                        <span className="bg-green-100 w-8 h-8 rounded-full flex items-center justify-center text-sm">{t('steps.step4.number')}</span>
                        <span>{t('steps.step4.label')}</span>
                    </div>
                    <div className="clear-both"></div>
                    <h3 className="text-3xl font-bold text-slate-900 mb-4">{t('steps.step4.title')}</h3>
                    <p className="text-lg text-slate-600 leading-relaxed">
                        {t('steps.step4.description')}
                    </p>
                </div>
            </div>

        </div>
      </section>

      {/* For Schools Section */}
      <section className="py-20 bg-slate-900 text-white">
         <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div className="space-y-6" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                    <Badge className="bg-blue-600 hover:bg-blue-700 border-none">{t('schools.badge')}</Badge>
                    <h2 className="text-3xl md:text-4xl font-bold leading-tight">
                        {t('schools.title')}
                    </h2>
                    <p className="text-slate-300 text-lg leading-relaxed">
                        {t('schools.description')}
                    </p>
                    
                    <div className="space-y-4 pt-4">
                        <div className={`flex items-start gap-4 w-full ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                                <Laptop className="w-5 h-5 text-blue-400" />
                            </div>
                            <div className="flex-1" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                                <h4 className="font-semibold text-white">{t('schools.features.equity.title')}</h4>
                                <p className="text-sm text-slate-400">{t('schools.features.equity.description')}</p>
                            </div>
                        </div>
                        <div className={`flex items-start gap-4 w-full ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                                <Sparkles className="w-5 h-5 text-purple-400" />
                            </div>
                            <div className="flex-1" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                                <h4 className="font-semibold text-white">{t('schools.features.noHallucinations.title')}</h4>
                                <p className="text-sm text-slate-400">{t('schools.features.noHallucinations.description')}</p>
                            </div>
                        </div>
                        <div className={`flex items-start gap-4 w-full ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                                <Trophy className="w-5 h-5 text-amber-400" />
                            </div>
                            <div className="flex-1" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                                <h4 className="font-semibold text-white">{t('schools.features.outcomes.title')}</h4>
                                <p className="text-sm text-slate-400">{t('schools.features.outcomes.description')}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Abstract Visual */}
                <div className={`relative h-[500px] bg-slate-800/50 rounded-3xl border border-slate-700 p-8 flex items-center justify-center overflow-hidden ${isRTL ? 'lg:order-first' : ''}`}>
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10"></div>
                    <div className="relative z-10 grid grid-cols-2 gap-6">
                         <Card className="bg-slate-800 border-slate-600 text-slate-200 w-40 h-40 flex flex-col items-center justify-center gap-3 hover:scale-105 transition-transform cursor-default shadow-xl">
                            <Bot className="w-10 h-10 text-blue-400" />
                            <span className="text-sm font-medium">{t('schools.cards.aiTutor')}</span>
                         </Card>
                         <Card className="bg-slate-800 border-slate-600 text-slate-200 w-40 h-40 flex flex-col items-center justify-center gap-3 hover:scale-105 transition-transform cursor-default shadow-xl translate-y-12">
                            <Users className="w-10 h-10 text-purple-400" />
                            <span className="text-sm font-medium">{t('schools.cards.students')}</span>
                         </Card>
                         <Card className="bg-slate-800 border-slate-600 text-slate-200 w-40 h-40 flex flex-col items-center justify-center gap-3 hover:scale-105 transition-transform cursor-default shadow-xl -translate-y-12">
                             <BookOpen className="w-10 h-10 text-green-400" />
                            <span className="text-sm font-medium">{t('schools.cards.curriculum')}</span>
                         </Card>
                         <Card className="bg-slate-800 border-slate-600 text-slate-200 w-40 h-40 flex flex-col items-center justify-center gap-3 hover:scale-105 transition-transform cursor-default shadow-xl">
                             <GraduationCap className="w-10 h-10 text-amber-400" />
                            <span className="text-sm font-medium">{t('schools.cards.teacher')}</span>
                         </Card>
                    </div>
                </div>
            </div>
         </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 text-center">
        <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{t('cta.title')}</h2>
            <p className="text-xl text-slate-600">
                {t('cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/courses">
                    <Button size="lg" className="h-14 px-10 text-lg bg-slate-900 hover:bg-slate-800 text-white rounded-full shadow-lg hover:shadow-xl transition-all w-full sm:w-auto">
                        {t('cta.exploreCourses')}
                        <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                    </Button>
                </Link>
                <Link to="/partners">
                    <Button variant="outline" size="lg" className="h-14 px-10 text-lg rounded-full w-full sm:w-auto border-slate-300 hover:bg-slate-50">
                        {t('cta.becomePartner')}
                    </Button>
                </Link>
            </div>
        </div>
      </section>

      {/* Footer - Same as Homepage */}
      <Footer />
    </div>
  );
}
